import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CreditCard, CalendarClock, AlarmClock, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TONES = {
  upcoming: { wrap: "border-emerald-200 bg-emerald-50", icon: "bg-emerald-600 text-white", text: "text-emerald-900", sub: "text-emerald-800/80", Icon: CalendarClock },
  due_today: { wrap: "border-amber-200 bg-amber-50", icon: "bg-amber-500 text-white", text: "text-amber-900", sub: "text-amber-800/80", Icon: AlarmClock },
  overdue: { wrap: "border-rose-200 bg-rose-50", icon: "bg-rose-600 text-white", text: "text-rose-900", sub: "text-rose-800/80", Icon: AlarmClock },
};

const headline = (n) => {
  if (n.state === "overdue") return `Rent is ${n.days} day${n.days === 1 ? "" : "s"} overdue`;
  if (n.state === "due_today") return "Rent is due today";
  return `Rent is due in ${n.days} day${n.days === 1 ? "" : "s"}`;
};

export function RentNudgeBanner({ nudge, leaseId, showOther = true }) {
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);
  if (!nudge?.show) return null;
  const t = TONES[nudge.state] || TONES.upcoming;
  const dueDate = new Date(nudge.due_date + "T12:00:00Z").toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

  const payCard = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { lease_id: leaseId, period: nudge.period, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setPaying(false); }
  };

  return (
    <div className={`rounded-2xl border p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-4 ${t.wrap}`} data-testid="rent-nudge-banner" data-state={nudge.state}>
      <span className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${t.icon}`}><t.Icon className="h-5 w-5" /></span>
      <div className="flex-1 min-w-[200px]">
        <div className={`font-display font-bold text-base sm:text-lg ${t.text}`} data-testid="rent-nudge-headline">{headline(nudge)}</div>
        <div className={`text-sm ${t.sub}`} data-testid="rent-nudge-detail">
          {fmtMoney(nudge.amount)} for {nudge.period_label} · due {dueDate}
          {nudge.fees > 0 && <> · includes {fmtMoney(nudge.fees)} in late fees</>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={payCard} disabled={paying} className={`gap-2 h-10 ${nudge.state === "overdue" ? "bg-rose-600 hover:bg-rose-700" : ""}`} data-testid="rent-nudge-pay">
          {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay {fmtMoney(nudge.amount)} by card
        </Button>
        {showOther && (
          <button type="button" onClick={() => navigate("/portal/payments")} className={`text-sm font-semibold inline-flex items-center gap-1 hover:underline ${t.text}`} data-testid="rent-nudge-other">
            Other ways to pay <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
